import arrayUtils from '@/utils/arrayUtils';

import marketConfigService from '../marketConfigService';
import {filterSectionsById} from '../pageSectionService';
import {getContent} from './content';

const getConnectionContent = () => getContent().connectionContent || {};
const getConsumerProfileContent = () => getContent().consumerProfile;
const getDeviceStatusContent = () => getContent().deviceStatusContent;
const getDeviceSettingContentList = () => getContent().deviceSettingContentList;
const getFirmwareUpdateContent = () => getContent().firmwareUpdate;
const getDeviceSettingsRestrictionNotifications = () => getContent().deviceSettingsRestrictionNotifications;
const getLoyaltyContent = () => getContent().loyaltyContent || {};
const getFinishWhatYouStartedContent = () => getLoyaltyContent().finishWhatYouStartedContent;
const getBadgesListContent = () => getLoyaltyContent().badgesListContent;
const getEarnBadgeContent = () => getLoyaltyContent().earnBadgeContent;
const getNewBadgeContent = () => getLoyaltyContent().newBadgeContent;
const getCompleteTaskContent = () => getLoyaltyContent().completeTaskContent;
const getNextTaskContent = () => getLoyaltyContent().nextTaskContent;
const getUsbRequiredPopupContent = () => getContent().usbRequiredPopup;
const getExpiringBadgeContent = () => getLoyaltyContent().expiringBadgeContent;

const getConnectionMethods = () => getConnectionContent().connectionMethods;
const getPairingGuide = () => getConnectionContent().pairingGuide;
const getConnectionGuide = () => getConnectionContent().connectionGuide;
const getClosePairingPopup = () => getConnectionContent().closePairingPopup;
const getCloseConnectingPopup = () => getConnectionContent().closeConnectingPopup;
const getBleConnectionFlow = () => getConnectionContent().bleConnectionFlow;
const getUsbConnectionFlow = () => getConnectionContent().usbConnectionFlow;
const getConnectionFinished = () => getConnectionContent().connectionFinished;
const getRegistrationErrorsContent = () => getConnectionContent().registrationErrorsContent;
const getConnectionErrorsContent = () => getConnectionContent().connectionErrorsContent;
const getHolderRegistrationPopup = () => getConnectionContent().holderRegistrationPopup;
const getUpdateFirmwareInfoBlock = () => getFirmwareUpdateContent().updateFirmwareInfoBlock;
const getUpdateFirmwareInfo = () => ({
    title: 'Software update',
    description:
        'This update gives you the option to use our new range of LEVIA zero- tobacco flavors with an optimal experience and get access to the overview of your usage statistics.',
    icon: {
        title: 'On',
        path: 'icons/FlexPuff.svg',
        alt: null,
    },
    continueButton: {
        text: 'Continue',
        alt: null,
    },
    laterButton: {
        text: 'I will do it later',
        alt: null,
    },
});
const getUpdateFirmwareNotice = () => ({
    title: 'Before you proceed',
    description: "To start the firmware upgrade, please press the 'Start Upgrade' button below.",
    icon: {
        title: 'On',
        path: 'icons/WarningIcon.svg',
        alt: null,
    },
    noticeIcon: {
        title: 'On',
        path: 'icons/TipsIcon.svg',
        alt: null,
    },
    instructions: {
        step1: 'After a few seconds a connection button will appear.',
        tip: "It is important that you click the 'Connect' button as soon as it appears, or you may have to start again.",
        step2: 'Depending on how many updates your IQOS device needs, you may need to do this during each update.',
    },
    continueButton: {
        text: 'Start Upgrade',
        alt: null,
    },
});
const getUpdateFirmwareUsbConnectionRequiredPopupContent = () => ({
    icon: {
        title: 'On',
        path: 'icons/ErrorIcon.svg',
        alt: null,
    },
    title: 'USB connection required',
    usbInstructionText:
        'Please plug your IQOS device into your mobile phone using an adapted USB cable to update your device firmware. An OTG connector could be required.',
    desktopUpdateText: 'Alternatively, you can perform the update with your computer using the IQOS desktop app',
    okButton: {
        text: 'OK got it',
        alt: null,
    },
});

const getNoHolderBanner = () => getDeviceSettingsRestrictionNotifications()?.holderDisconnectedInfoIcon;
const getNoHolderUsbBanner = () => getDeviceSettingsRestrictionNotifications()?.usbHolderDisconnectedInfoIcon;
const getHolderReconnectingBanner = () => getDeviceSettingsRestrictionNotifications()?.holderReconnectingInfoIcon;
const getHolderDetectedInfoIcon = () => getDeviceSettingsRestrictionNotifications()?.holderDetectedInfoIcon;
const getNoHolderNotification = () => {
    const notifications = getDeviceSettingsRestrictionNotifications() || {};
    const title = notifications.holderDisconnectedTitle;
    const description = notifications.holderDisconnectedDescription;
    const button = notifications.holderDisconnectedButton;

    return {title, description, button};
};
const getNoHolderUsbNotification = () => {
    const notifications = getDeviceSettingsRestrictionNotifications() || {};
    const title = notifications.usbHolderDisconnectedTitle;
    const description = notifications.usbHolderDisconnectedDescription;
    const button = notifications.usbHolderDisconnectedButton;

    return {title, description, button};
};
const getHolderSettingChangeFailedNotification = () => {
    const notifications = getDeviceSettingsRestrictionNotifications() || {};
    const title = notifications.holderSettingChangeFailedTitle;
    const description = notifications.holderSettingChangeFailedDescription;

    return {title, description};
};

const getPushNotificationsSettingsContent = () => getConsumerProfileContent().pushNotificationsSettings;
const getNotificationTurnOnPopups = () => getPushNotificationsSettingsContent()?.turnOnPopups;

const getNotificationTurnOnPhonePopupByPlatform = () => {
    const popups = getNotificationTurnOnPopups();
    const popup = marketConfigService.findPageByPlatform(popups);
    const itemsSorted = arrayUtils.sortArrayWithOrderField(popup?.items);

    popup.items = itemsSorted;

    return popup;
};

const getNotificationPromptPopup = () => {
    const popup = getPushNotificationsSettingsContent()?.promptPopups?.[0];

    if (!popup) return;

    const {title, button} = popup;

    if (!title || !button) return;

    const itemsSorted = arrayUtils.sortArrayWithOrderField(popup.items);

    popup.items = itemsSorted;

    return popup;
};

const getUpdatePushNotificationsFailedNotification = () => {
    const {
        updateFailedButton: {text: buttonText},
        updateFailedDescription: description,
        updateFailedTitle: title,
    } = getPushNotificationsSettingsContent() || {};

    return {title, description, buttonText};
};

const getTopBannerById = (id) => {
    const banners = getContent().topBanners;
    const topBannerData = filterSectionsById({list: banners, id})[0];

    return topBannerData;
};

const getGlobalButtons = () => getContent().globalButtons;

export default {
    getConnectionMethods,
    getConnectionGuide,
    getConnectionErrorsContent,
    getPairingGuide,
    getCloseConnectingPopup,
    getClosePairingPopup,
    getBleConnectionFlow,
    getUsbConnectionFlow,
    getConnectionFinished,
    getHolderRegistrationPopup,
    getRegistrationErrorsContent,
    getDeviceStatusContent,
    getDeviceSettingContentList,
    getUpdateFirmwareInfoBlock,
    getNoHolderBanner,
    getNoHolderUsbBanner,
    getHolderReconnectingBanner,
    getHolderDetectedInfoIcon,
    getFirmwareUpdateContent,
    getUpdateFirmwareInfo,
    getUpdateFirmwareNotice,
    getUpdateFirmwareUsbConnectionRequiredPopupContent,
    getNoHolderNotification,
    getNoHolderUsbNotification,
    getHolderSettingChangeFailedNotification,
    getUsbRequiredPopupContent,

    getPushNotificationsSettingsContent,
    getNotificationPromptPopup,
    getNotificationTurnOnPhonePopupByPlatform,
    getUpdatePushNotificationsFailedNotification,
    getNextTaskContent,
    getLoyaltyContent,
    getFinishWhatYouStartedContent,
    getBadgesListContent,
    getEarnBadgeContent,
    getTopBannerById,
    getNewBadgeContent,
    getCompleteTaskContent,
    getGlobalButtons,
    getExpiringBadgeContent,
};
